/**
 * Request state for screens. Every screen in flows.json needs at least
 * loading / error / ready, so that is exactly what this returns — and it keeps
 * the previous payload around so `analyzing` can render the last completed
 * analysis instead of a blank page.
 */
import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react'
import { ApiFailure, asApiFailure } from './api'
import { useOriginRetry } from './origin'

export type Async<T> =
  | { status: 'loading'; data?: T }
  | { status: 'ready'; data: T }
  | { status: 'error'; error: ApiFailure; data?: T }

export interface AsyncResult<T> {
  state: Async<T>
  reload: () => void
  /** Optimistic updates write through this and roll back by reloading. */
  setData: (updater: T | ((previous: T) => T)) => void
}

export function useAsync<T>(load: (signal: AbortSignal) => Promise<T>, deps: DependencyList): AsyncResult<T> {
  const [state, setState] = useState<Async<T>>({ status: 'loading' })
  const [nonce, setNonce] = useState(0)
  const loadRef = useRef(load)
  loadRef.current = load

  useEffect(() => {
    const controller = new AbortController()
    let live = true
    setState((previous) => ({ status: 'loading', data: previous.data }))
    loadRef
      .current(controller.signal)
      .then((data) => {
        if (live) setState({ status: 'ready', data })
      })
      .catch((e: unknown) => {
        if (!live || (e as Error)?.name === 'AbortError') return
        setState((previous) => ({ status: 'error', error: asApiFailure(e), data: previous.data }))
      })
    return () => {
      live = false
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  // What serviceUnavailable's Retry re-issues. Every mounted loader offers its
  // own reload, so pressing Retry re-runs exactly the requests the screen was
  // making — it never reloads the page, which would drop the data already here.
  useOriginRetry(reload)

  const setData = useCallback((updater: T | ((previous: T) => T)) => {
    setState((previous) => {
      const base = previous.data
      if (base === undefined) return previous
      const next = typeof updater === 'function' ? (updater as (p: T) => T)(base) : updater
      return { status: 'ready', data: next }
    })
  }, [])

  return { state, reload, setData }
}

/** Collapse several requests into one screen state. Loading wins, then error. */
export function combine<A, B>(a: Async<A>, b: Async<B>): Async<[A, B]>
export function combine<A, B, C>(a: Async<A>, b: Async<B>, c: Async<C>): Async<[A, B, C]>
export function combine<A, B, C, D>(a: Async<A>, b: Async<B>, c: Async<C>, d: Async<D>): Async<[A, B, C, D]>
export function combine(...parts: Async<unknown>[]): Async<unknown[]> {
  const failed = parts.find((p) => p.status === 'error')
  const partial = parts.map((p) => p.data)
  const allData = parts.every((p) => p.data !== undefined)
  if (failed && failed.status === 'error') {
    return { status: 'error', error: failed.error, data: allData ? partial : undefined }
  }
  if (parts.some((p) => p.status === 'loading')) {
    return { status: 'loading', data: allData ? partial : undefined }
  }
  return { status: 'ready', data: partial }
}
